import { isDeepEqual } from '@attain-apps/data-utils/is-equal' // NOTE: longer path avoids circular dependency
import { UserFriendlyError } from '@attain-apps/errors'
import { assignProperties } from '@attain-apps/object-utils'
import { format } from 'pretty-format'

import { TypeChecker } from './type-checker'
import type { TypeGuard } from './type-guard'

const TYPE_ASSERTER_SYMBOL = Symbol.for('@attain-apps.TypeAsserter')

export type TypeAsserter<T, TIn = unknown> = TypeAsserter.As<
  TypeAsserter.Function<T, TIn>
>
export namespace TypeAsserter {
  export const isTypeAsserter: TypeChecker<TypeAsserter<unknown>> =
    TypeChecker.Factory.markerSymbol(TYPE_ASSERTER_SYMBOL)

  /**
   * This can be used to add type parameters to the type checking function
   *
   * @example
   * const assertIsList: TypeAsserter.As<<T>(val: any) => asserts val is T[]> = ...
   * assertIsList<number>([ 1, 2, 3 ])
   */
  export type As<TFn extends Function<any, any> | Function<never, never>> =
    TFn &
      TypeGuard.Props & {
        /**
         * @see {@link TypeChecker.raw}
         */
        readonly raw: TFn
      }

  export type Function<T, TIn = unknown> = (
    val: TIn,
    errorMessage?: string,
  ) => asserts val is TIn & T

  // NOTE: it is important that we do not pass `any` as the second type
  // parameter when inferring as that causes issues with `assertIsNotNullish`
  export type ExtractTypeFrom<TAsserter extends TypeAsserter<any, any>> =
    TAsserter extends TypeAsserter<infer T> ? T : never

  export type ErrorProducer<T> = (
    typeGuard: TypeGuard<T>,
    val: unknown,
  ) => Error

  export class AssertionError extends UserFriendlyError {
    /**
     * Type descriptions can be auto-generated. If they are auto-generated, they
     * may be overly-complex and/or reveal sensitive information, and therefore
     * not user-friendly. This RegExp tests for simply type descriptions
     */
    public static readonly userFriendlyTypeDescriptionRegExp = /^[\w.]+$/

    public override readonly name: string = 'TypeAssertionError'

    public readonly props: Readonly<AssertionError.Props>

    constructor({ message, typeDescription, value }: AssertionError.Params) {
      const messageForUser =
        message?.forUser ??
        `Encountered invalid ${
          AssertionError.userFriendlyTypeDescriptionRegExp.test(typeDescription)
            ? typeDescription
            : 'data type'
        }`
      const messageForLogs =
        message?.forLogs ??
        `Expected type: ${typeDescription}. Received: ${format(value)}`
      super(messageForUser, new Error(messageForLogs))
      this.props = { typeDescription, value }
    }
  }
  export namespace AssertionError {
    export interface Props extends Pick<TypeGuard.Props, 'typeDescription'> {
      value: unknown
    }

    export interface Params extends Props {
      message?: {
        /**
         * Displayed to an end user
         *
         * @default something generic with no information about the specific type or value
         */
        forUser?: string
        /**
         * Shown in the logs
         *
         * @default includes information about the type and value
         */
        forLogs?: string
      }
    }
  }

  export namespace Factory {
    export function custom<
      TFn extends Function<any, any> | Function<never, never>,
    >(fn: TFn, props: TypeGuard.Props): TypeAsserter.As<TFn> {
      // NOTE: weird [never] type is used to avoid issues with spreading
      // arguments for a function that does not have a rest parameter
      return assignProperties(
        // Spreading args in case optional args as used by fn
        ((...args: [never]) => fn(...args)) as typeof fn,
        props,
        {
          [TYPE_ASSERTER_SYMBOL]: true,
          raw: fn,
        },
      )
    }

    export function from<T>(
      typeChecker: TypeChecker<T>,
      { customError }: from.Options<T> = {},
    ): TypeAsserter<T> {
      return assignProperties(
        custom<Function<T>>(
          (value: unknown, errorMessage) => {
            if (!typeChecker(value)) {
              throw customError
                ? customError(typeChecker, value)
                : new AssertionError({
                    message: errorMessage
                      ? { forLogs: errorMessage, forUser: errorMessage }
                      : undefined,
                    typeDescription: typeChecker.typeDescription,
                    value,
                  })
            }
          },
          {
            get typeDescription(): string {
              return typeChecker.typeDescription
            },
          },
        ),
        { typeChecker } satisfies HasTypeChecker,
      )
    }
    export namespace from {
      export interface Options<T> {
        customError?: ErrorProducer<T>
      }
    }
  }

  /**
   * Applies the given type asserters to the given value and then returns the value
   *
   * Useful if you want to use a TypeAsserter in an arrow function without a body
   *
   * @example
   * const ids = items.map(item => TypeAsserter.validate(item.id, TypeAsserter.assertIsNotNullish))
   */
  export function validate<T>(val: T): T
  export function validate<
    TIn,
    TAsserters extends readonly (
      | TypeAsserter<any, TIn>
      | readonly [TypeAsserter<any, TIn>, message?: string]
    )[],
  >(
    val: TIn,
    ...asserters: TAsserters
  ): TIn &
    TupleToIntersection<{
      [K in keyof TAsserters]: ExtractTypeFrom<
        | Extract<TAsserters[K], TypeAsserter<any, TIn>>
        | Extract<
            Extract<TAsserters[K], readonly unknown[]>[0],
            TypeAsserter<any, TIn>
          >
      >
    }>
  export function validate(
    val: unknown,
    ...assertersMaybeWithMessages: (
      | TypeAsserter<unknown, unknown>
      | [TypeAsserter<unknown, unknown>, message?: string]
    )[]
  ): unknown {
    for (const asserterMaybeWithMessage of assertersMaybeWithMessages) {
      let asserter: TypeAsserter<unknown, unknown>
      let errorMessage: string | undefined
      if (TypeChecker.isReadonlyArray(asserterMaybeWithMessage)) {
        asserter = asserterMaybeWithMessage[0]
        errorMessage = asserterMaybeWithMessage[1]
      } else {
        asserter = asserterMaybeWithMessage
      }
      asserter(val, errorMessage)
    }
    return val
  }

  /**
   * Similar to {@link validate}, but instead of throwing an error, it just logs
   * a warning. This is useful in contexts where it is better to try and proceed
   * then guaranteeing a failure. For example, on the frontend, when receiving
   * data from the backend that is not 100% structured correctly (e.g. missing a
   * field), it might be better to try and use/display whatever you can, rather
   * than just have the whole page show an error.
   *
   * @example
   * const myItem = TypeAsserter.validateButJustWarn(getResult.item, MyItem.assertIsMyItem)
   */
  export function validateButJustWarn<T>(val: T): T
  // TODO: support error messages, similar to validate
  export function validateButJustWarn<
    T,
    TAsserters extends readonly TypeAsserter<any, T>[],
  >(
    val: T,
    ...asserters: TAsserters
  ): T &
    TupleToIntersection<{
      [K in keyof TAsserters]: ExtractTypeFrom<TAsserters[K]>
    }>
  export function validateButJustWarn(
    val: unknown,
    ...asserters: readonly TypeAsserter<unknown, unknown>[]
  ): unknown {
    // Using try-catch block is not very performant, so we first try and use
    // type checkers if available
    const assertersWithoutTypeCheckers: TypeAsserter<unknown, unknown>[] = []
    let err: Error | undefined
    for (const asserter of asserters) {
      const { typeChecker } = asserter as Partial<HasTypeChecker>
      if (!typeChecker) {
        // If this asserter does not have a type checker, we must store it to be
        // used in the try-catch block below
        assertersWithoutTypeCheckers.push(asserter)
        continue
      }
      // If this asserter has a type checker, we use it to determine if the
      // value matches the type
      if (!typeChecker(val)) {
        // If it does not, we create an error to put in the warning below
        err = new AssertionError({
          typeDescription: typeChecker.typeDescription,
          value: val,
        })
        break
      }
    }
    if (!err && assertersWithoutTypeCheckers.length) {
      // If the type checkers didn't produce any errors, and there are asserters
      // without type checkers, we fallback on the regular validate method
      // wrapped in a try-catch
      try {
        validate(val, ...assertersWithoutTypeCheckers)
      } catch (_err) {
        err = _err as Error
      }
    }
    if (err) {
      // For user-friendly errors, we want to show the actual error since that
      // is more useful. We cast so that we can get the actual error, if it
      // exists
      const { actualError } = (err ?? {}) as Partial<UserFriendlyError>
      console.warn(actualError ?? err)
    }
    // Regardless of whether or not there are issues, we return a value
    return val
  }

  /**
   * Asserts that the first value is equal to (and thus has the same type as)
   * the second value
   */
  export function assertIsEqual<T>(
    val1: unknown,
    val2: T,
    errorMessage?: string,
  ): asserts val1 is T {
    if (!isDeepEqual(val1, val2)) {
      throw new AssertionError({
        message: { forUser: errorMessage },
        typeDescription: format(val2),
        value: val1,
      })
    }
  }

  export const assertIsString: TypeAsserter<string> = TypeAsserter.Factory.from(
    TypeChecker.isString,
  )
  export const assertIsNumber: TypeAsserter<number> = TypeAsserter.Factory.from(
    TypeChecker.isNumber,
  )
  export const assertIsBigInt: TypeAsserter<bigint> = TypeAsserter.Factory.from(
    TypeChecker.isBigInt,
  )
  export const assertIsBoolean: TypeAsserter<boolean> =
    TypeAsserter.Factory.from(TypeChecker.isBoolean)
  export const assertIsTrue: TypeAsserter<true> = TypeAsserter.Factory.from(
    TypeChecker.isTrue,
  )
  export const assertIsFalse: TypeAsserter<false> = TypeAsserter.Factory.from(
    TypeChecker.isFalse,
  )
  export const assertIsSymbol: TypeAsserter<symbol> = TypeAsserter.Factory.from(
    TypeChecker.isSymbol,
  )
  export const assertIsObject: TypeAsserter<object> = TypeAsserter.Factory.from(
    TypeChecker.isObject,
  )
  export const assertIsFunction: TypeAsserter<globalThis.Function> =
    TypeAsserter.Factory.from(TypeChecker.isFunction)
  export const assertIsDate: TypeAsserter<Date> = TypeAsserter.Factory.from(
    TypeChecker.isDate,
  )
  export const assertIsRegExp: TypeAsserter<RegExp> = TypeAsserter.Factory.from(
    TypeChecker.isRegExp,
  )
  export const assertIsUrl: TypeAsserter<URL> = TypeAsserter.Factory.from(
    TypeChecker.isUrl,
  )
  export const assertIsIterable: TypeAsserter<Iterable<unknown>> =
    TypeAsserter.Factory.from(TypeChecker.isIterable)
  export const assertIsIterator: TypeAsserter<Iterator<unknown>> =
    TypeAsserter.Factory.from(TypeChecker.isIterator)
  export const assertIsIterableIterator: TypeAsserter<
    IterableIterator<unknown>
  > = TypeAsserter.Factory.from(TypeChecker.isIterableIterator)
  export const assertIsArray: TypeAsserter<any[]> = TypeAsserter.Factory.from(
    TypeChecker.isArray,
  )
  export const assertIsReadonlyArray: TypeAsserter<readonly unknown[]> =
    TypeAsserter.Factory.from(TypeChecker.isReadonlyArray)
  export const assertIsSet: TypeAsserter<Set<unknown>> =
    TypeAsserter.Factory.from(TypeChecker.isSet)
  export const assertIsReadonlySet: TypeAsserter<ReadonlySet<unknown>> =
    TypeAsserter.Factory.from(TypeChecker.isReadonlySet)
  export const assertIsNull: TypeAsserter<null> = TypeAsserter.Factory.from(
    TypeChecker.isNull,
  )
  export const assertIsUndefined: TypeAsserter<undefined> =
    TypeAsserter.Factory.from(TypeChecker.isUndefined)
  export const assertIsNullish: TypeAsserter<Nullish> =
    TypeAsserter.Factory.from(TypeChecker.isNullish)
  export const assertIsNotNullish: TypeAsserter.As<
    <T>(
      val: T | Nullish,
      errorMessage?: string,
    ) => asserts val is NonNullable<T>
  > = TypeAsserter.Factory.from(TypeChecker.isNotNullish)
  export const assertIsVoid: TypeAsserter<void> = TypeAsserter.Factory.from(
    TypeChecker.isVoid,
  )
  /**
   * Used to assert that all possible types of a value have previously been
   * handled by if statements or switch cases. I.e., if all cases are handled,
   * this should never be called.
   */
  export const assertIsNever: TypeAsserter<never, never> = Factory.custom(
    (value: never): asserts value is never => {
      throw new AssertionError({
        message: {
          forLogs: `assertIsNever was called! Received: ${format(value)}`,
        },
        typeDescription: TypeChecker.isNever.typeDescription,
        value,
      })
    },
    { typeDescription: TypeChecker.isNever.typeDescription },
  )
  export const assertIsAny: TypeAsserter<any> = TypeAsserter.Factory.from(
    TypeChecker.isAny,
  )
  export const assertIsUnknown: TypeAsserter<unknown> =
    TypeAsserter.Factory.from(TypeChecker.isUnknown)
  export const assertIsPrimitive: TypeAsserter<Primitive> =
    TypeAsserter.Factory.from(TypeChecker.isPrimitive)
}

/**
 * Adding this as an optional property directly to {@link TypeAsserter} causes
 * problems with inferring types, so we use this privately in this file, mostly
 * just to optimize {@link TypeAsserter.validateButJustWarn}
 */
interface HasTypeChecker {
  typeChecker: TypeChecker<unknown>
}
